import React, { useState } from 'react';
import { Mail, Shield, Check } from 'lucide-react';
import { Button, Input, LoadingSpinner } from '../UIComponents';
import { usersApi, rolesApi } from '../../lib/permissions';
import type { PortalUserFull, Role, RoleName } from '../../types/permissions';

const ROLE_STYLES: Record<RoleName, { bg: string; border: string; text: string; description: string }> = {
  superuser: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    description: 'Full access to all companies and portal management',
  },
  administrator: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    description: 'Full access to assigned companies',
  },
  customer_manager: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    description: 'Limited management of assigned customers',
  },
  user: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-700',
    description: 'Read-only access to assigned customers',
  },
};

interface UserFormProps {
  user: PortalUserFull;
  roles: Role[];
  onSave: () => void;
  onCancel: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({ user, roles, onSave, onCancel }) => {
  const [firstName, setFirstName] = useState(user.first_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');
  const [isActive, setIsActive] = useState(user.is_active);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(
    user.roles.length > 0
      ? user.roles.reduce((prev, curr) =>
          curr.hierarchy_level < prev.hierarchy_level ? curr : prev
        ).id
      : null
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Update user details
      await usersApi.update(user.id, {
        first_name: firstName || null,
        last_name: lastName || null,
        is_active: isActive,
      });

      // Update role if changed
      const currentRoleId = user.roles.length > 0
        ? user.roles.reduce((prev, curr) =>
            curr.hierarchy_level < prev.hierarchy_level ? curr : prev
          ).id
        : null;

      if (selectedRoleId !== currentRoleId) {
        if (selectedRoleId) {
          const selectedRole = roles.find((r) => r.id === selectedRoleId);
          if (selectedRole) {
            await rolesApi.setUserRole(user.id, selectedRole.name as RoleName);
          }
        } else {
          // Remove all roles if none selected
          for (const role of user.roles) {
            await rolesApi.removeRole(user.id, role.id);
          }
        }
      }

      onSave();
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* User Info */}
      <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-accent text-white rounded-xl flex items-center justify-center font-bold text-xl">
            {(firstName?.[0] || user.email[0]).toUpperCase()}
          </div>
          <div>
            <p className="font-bold text-slate-900">
              {firstName && lastName ? `${firstName} ${lastName}` : user.email.split('@')[0]}
            </p>
            <p className="text-sm text-slate-500 flex items-center gap-1">
              <Mail size={14} />
              {user.email}
            </p>
          </div>
        </div>
      </div>

      {/* Name Fields */}
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="John"
        />
        <Input
          label="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Doe"
        />
      </div>

      {/* Role Selection */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-3">
          <Shield size={14} className="inline mr-1" />
          Role
        </label>
        <div className="space-y-2">
          {roles.map((role) => {
            const style = ROLE_STYLES[role.name as RoleName] || ROLE_STYLES.user;
            const isSelected = selectedRoleId === role.id;

            return (
              <button
                key={role.id}
                type="button"
                onClick={() => setSelectedRoleId(isSelected ? null : role.id)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                  isSelected
                    ? `${style.bg} ${style.border} ring-2 ring-offset-2 ring-${style.border.replace('border-', '')}`
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isSelected ? style.bg : 'bg-slate-100'
                    }`}
                  >
                    {isSelected ? (
                      <Check size={16} className={style.text} />
                    ) : (
                      <Shield size={16} className="text-slate-400" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className={`font-semibold ${isSelected ? style.text : 'text-slate-700'}`}>
                      {role.display_name}
                    </p>
                    <p className="text-xs text-slate-500">{style.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {!selectedRoleId && (
          <p className="text-sm text-amber-600 mt-2">
            No role selected. User will see "Access Pending" message.
          </p>
        )}
      </div>

      {/* Active Status */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
        <div>
          <p className="font-semibold text-slate-700">Account Status</p>
          <p className="text-sm text-slate-500">
            Inactive users cannot access the portal
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
        </label>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="flex-1">
          {saving ? <LoadingSpinner size="sm" /> : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
};
